import {
    ActionItem,
    ActionItemStyle,
    AsyncEllipsisMultiActionItem,
    EllipsisMultiActionItem,
    OpenDirection
} from '@stylehaven/components';
import { flatMap } from 'lodash-es';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

export function ReduceVisibleActions(
    actions: ActionItem[],
    style: ActionItemStyle = ActionItemStyle.PureSquareIconButtonLight,
    openDirection: OpenDirection = OpenDirection.BelowLeft,
    maxVisible: number = 2
): ActionItem[] {
    if (maxVisible < 0) {
        maxVisible = 0;
    }
    if (actions.length > maxVisible + 1) {
        const toShow: ActionItem[] = actions.slice(0, maxVisible);
        const toHide: ActionItem[] = actions.slice(maxVisible);

        const moreActions: ActionItem = EllipsisMultiActionItem(toHide, false, style, openDirection);

        return [...toShow, moreActions];
    }

    return actions;
}

export function AsyncReduceVisibleActions(
    actions$: Observable<ActionItem | null>[],
    style: ActionItemStyle = ActionItemStyle.PureSquareIconButtonLight,
    openDirection: OpenDirection = OpenDirection.BelowLeft,
    maxVisible: number = 2
): Observable<ActionItem[]> {
    if (actions$.length === 0) {
        return of([]);
    }

    if (maxVisible <= 0) {
        return of([AsyncEllipsisMultiActionItem(actions$, false, style, openDirection)]);
    }

    if (actions$.length <= maxVisible) {
        return combineLatest(actions$).pipe(map(actions => actions.filter((action): action is ActionItem => !!action)));
    }

    return combineLatest(actions$.slice(0, maxVisible)).pipe(
        map(actions => actions.filter((action): action is ActionItem => !!action)),
        switchMap(actions =>
            combineLatest([
                of(actions),
                evaluateActionsFromIndex(actions$, maxVisible, maxVisible - actions.length, style, openDirection)
            ])
        ),
        map(actions => flatMap(actions))
    );
}

function evaluateActionsFromIndex(
    actions$: Observable<ActionItem | null>[],
    index: number,
    amount: number,
    style: ActionItemStyle = ActionItemStyle.PureSquareIconButtonLight,
    openDirection: OpenDirection = OpenDirection.BelowLeft
): Observable<ActionItem[]> {
    if (amount === 0) {
        return of([AsyncEllipsisMultiActionItem(actions$.slice(index), false, style, openDirection)]);
    }

    return combineLatest(actions$.slice(index, index + amount)).pipe(
        map(actions => actions.filter((action): action is ActionItem => !!action)),
        switchMap(actions =>
            combineLatest([
                of(actions),
                evaluateActionsFromIndex(actions$, index + amount, amount - actions.length, style, openDirection)
            ]).pipe(map(_actions => flatMap(_actions)))
        )
    );
}
